/**
 * StarBurst — 8 star emojis burst outward in a circle, then fade
 * Used for send button feedback and achievement unlocks
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface StarBurstProps {
  trigger: boolean;
  duration?: number;
  emoji?: string;
}

export default function StarBurst({ trigger, duration = 800, emoji = '⭐' }: StarBurstProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (trigger) {
      setShow(true);
      const timer = setTimeout(() => setShow(false), duration);
      return () => clearTimeout(timer);
    }
  }, [trigger, duration]);

  const count = 8;
  const angleStep = (2 * Math.PI) / count;

  return (
    <AnimatePresence>
      {show && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
          {Array.from({ length: count }, (_, i) => {
            const angle = i * angleStep;
            const x = Math.cos(angle) * 40;
            const y = Math.sin(angle) * 40;
            return (
              <motion.span
                key={i}
                className="absolute text-sm"
                initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                animate={{
                  x,
                  y,
                  scale: [0, 1.2, 0.8],
                  opacity: [1, 1, 0],
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: duration / 1000,
                  delay: i * 0.03,
                  ease: 'easeOut',
                }}
              >
                {emoji}
              </motion.span>
            );
          })}
        </div>
      )}
    </AnimatePresence>
  );
}
